import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { createPurchaseOrder } from 'purchase-motions-api/mutation/purchaseOrderGeneration';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useIsSeats } from '../../../hooks/useIsSeats';
import { PurchaseMotions } from '../../../types/PurchaseMotion';
import { incrementPurchaseOrderGenerationFailure, incrementPurchaseOrderGenerationSuccess } from '../../../utils/metrics';
import { getQueryParams, getRequestedProductAsQueryParams } from '../../../utils/queryParams';
import { isUpgradePoint } from '../../../utils/ums';
import { getCheckoutUrl, getInterstitialUrl, navigateTo } from '../../../utils/urls';
import { tracker } from '../../../utils/usageTracker';
import { AdminModal } from '../../AdminModal/AdminModal';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { TalkToSalesButton } from '../TalkToSalesButton/TalkToSalesButton';
import { getBuyingButtonTrackingProperties, trackClickedBuyingButton, trackInterstitialRedirect, trackPurchaseOrderGenerationFailed, trackPurchaseOrderGenerationSuccess, trackViewBuyingButton } from './buying-button-trackers';
import { logPurchaseOrderError } from './purchase-order-error-logger';

/**
 * This component is used to render a button that allows the user to buy a product.
 * It is used for all buying purchase motions.
 * It internally handles the logic for interstitials, admin modal, and purchase order generation.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BuyingButtonBase = ({
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  apiName,
  children,
  currencyCode,
  location,
  purchaseMotion,
  requestedProducts,
  redirectQueryParams,
  shouldDisplayAlertOnFailure = true,
  skipInterstitial,
  terms,
  trackingData,
  uiButtonProps,
  onClick,
  onPurchaseOrderGenerationSuccess,
  onPurchaseOrderGenerationFailure,
  onInterstitialRedirect
}) => {
  const buyingButtonUsageTrackingProperties = getBuyingButtonTrackingProperties(purchaseMotion, trackingData, {
    apiName,
    requestedProducts
  });
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [purchaseOrderGenerating, setPurchaseOrderGenerating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const alertStore = useRef(FloatingAlertStore.newFloatingAlertStore());
  const isSeats = useIsSeats();
  const isAdmin = useIsAdmin('BILLING');

  // wait for the admin and seats checks to complete before showing the button
  const buttonReady = isAdmin !== null && isSeats !== null;

  // check which purchase motions are eligible for an interstitial
  // we need this check because seats and pre-seats addtl. contacts use a shared interstitial
  const isPurchaseMotionEligibleForInterstitial = purchaseMotion === PurchaseMotions.BUY_ADDITIONAL_CONTACTS || isSeats && (purchaseMotion === PurchaseMotions.BUY_TOUCHLESS || purchaseMotion === PurchaseMotions.BUY_ADDITIONAL_SEATS);
  const isOnInterstitialPage = location === 'PRICING_PAGE_INTERSTITIAL';
  const shouldGoToInterstitial = isPurchaseMotionEligibleForInterstitial && !isOnInterstitialPage && !skipInterstitial;
  useEffect(() => {
    trackViewBuyingButton(buyingButtonUsageTrackingProperties);
  }, [buyingButtonUsageTrackingProperties]);
  const handleClick = async () => {
    onClick === null || onClick === void 0 || onClick(purchaseMotion);
    if (!isAdmin) {
      setAdminModalOpen(true);
      return;
    }

    // go to interstitial
    if (shouldGoToInterstitial) {
      var _terms$paymentTerm;
      trackInterstitialRedirect(buyingButtonUsageTrackingProperties);
      if (isUpgradePoint(location)) {
        tracker.track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, buyingButtonUsageTrackingProperties));
      }
      const queryParams = getQueryParams();
      const productsAsQueryParams = getRequestedProductAsQueryParams(requestedProducts);
      const interstitialUrl = getInterstitialUrl(Object.assign({
        upgradeSource: (trackingData === null || trackingData === void 0 ? void 0 : trackingData.source) || '',
        purchaseMotion
      }, queryParams, productsAsQueryParams, currencyCode ? {
        currencyCode
      } : {}, terms !== null && terms !== void 0 && terms.paymentTerm ? {
        term: terms === null || terms === void 0 || (_terms$paymentTerm = terms.paymentTerm) === null || _terms$paymentTerm === void 0 ? void 0 : _terms$paymentTerm.toLowerCase()
      } : {}));
      onInterstitialRedirect === null || onInterstitialRedirect === void 0 || onInterstitialRedirect();
      navigateTo(interstitialUrl);
      return;
    }
    trackClickedBuyingButton(buyingButtonUsageTrackingProperties);
    const request = {
      commitmentTerm: terms.commitmentTerm,
      currencyCode,
      paymentTerm: terms.paymentTerm,
      products: requestedProducts.map(({
        productApiName,
        quantityPackApiName,
        quantity
      }) => ({
        // make quantityPackApiName compatible with the current POG service request shape
        productApiName: quantityPackApiName || productApiName,
        quantity: quantity || 1
      }))
    };
    const apiNames = requestedProducts.map(product => product.productApiName);

    // generate purchase order
    try {
      setHasError(false);
      setPurchaseOrderGenerating(true);
      const {
        purchaseOrderId
      } = await createPurchaseOrder(request);
      trackPurchaseOrderGenerationSuccess(buyingButtonUsageTrackingProperties);
      incrementPurchaseOrderGenerationSuccess(apiNames);
      const checkoutUrl = getCheckoutUrl(purchaseOrderId, {
        upgradeSource: trackingData === null || trackingData === void 0 ? void 0 : trackingData.source
      });
      const redirectUrl = new URL(checkoutUrl);

      // add any redirect params to the checkout URL if provided
      if (redirectQueryParams) {
        Object.entries(redirectQueryParams).forEach(([key, value]) => {
          redirectUrl.searchParams.append(key, value.toString());
        });
      }
      onPurchaseOrderGenerationSuccess === null || onPurchaseOrderGenerationSuccess === void 0 || onPurchaseOrderGenerationSuccess(purchaseOrderId);
      navigateTo(redirectUrl.toString());
    } catch (error) {
      trackPurchaseOrderGenerationFailed(buyingButtonUsageTrackingProperties);
      incrementPurchaseOrderGenerationFailure(apiNames);
      if (shouldDisplayAlertOnFailure) {
        setHasError(true);
        alertStore.current.addAlert({
          id: 'purchase-order-generation-failure',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buyNowFailureAlert.title"
          }),
          message: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "purchaseMotions.buyNowFailureAlert.message"
            }), /*#__PURE__*/_jsx(ButtonWrapper, {
              children: /*#__PURE__*/_jsx(Button, {
                use: "tertiary",
                size: "extra-small",
                onClick: () => {
                  var _document$getElementB;
                  alertStore.current.removeAlert('purchase-order-generation-failure');
                  (_document$getElementB = document.getElementById('hidden-talk-to-sales-button')) === null || _document$getElementB === void 0 || _document$getElementB.click();
                },
                children: "Talk to Sales"
              })
            })]
          }),
          type: 'danger'
        });
      }
      const errorToLog = error instanceof Error ? error : new Error('Unknown error', {
        cause: error
      });
      logPurchaseOrderError(errorToLog, Object.assign({}, buyingButtonUsageTrackingProperties, {
        apiNames,
        buttonName: `${purchaseMotion}Button`,
        request
      }));
      onPurchaseOrderGenerationFailure === null || onPurchaseOrderGenerationFailure === void 0 || onPurchaseOrderGenerationFailure(new Error('Purchase order generation failed', {
        cause: error
      }));
    } finally {
      setPurchaseOrderGenerating(false);
    }
  };
  const buttonText = useMemo(() => {
    if (children) {
      return children;
    }
    switch (purchaseMotion) {
      case PurchaseMotions.BUY_ADDITIONAL_SEATS:
        {
          return isSeats ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buttons.buyAdditionalSeats"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buttons.buyAdditionalUsers"
          });
        }
      case PurchaseMotions.BUY_ADDITIONAL_CONTACTS:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.buyAdditionalContacts"
        });
      case PurchaseMotions.BUY_TOUCHLESS:
      default:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.buyTouchless"
        });
    }
  }, [children, purchaseMotion, isSeats]);
  if (!buttonReady || purchaseOrderGenerating) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: true,
      children: buttonText
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldDisplayAlertOnFailure && /*#__PURE__*/_jsx(FloatingAlertList, {
      alertStore: alertStore.current
    }), shouldDisplayAlertOnFailure && hasError && /*#__PURE__*/_jsx(View, {
      display: "none",
      children: /*#__PURE__*/_jsx(TalkToSalesButton, {
        apiName: apiName,
        location: location,
        trackingData: Object.assign({}, trackingData, {
          uniqueId: 'checkout-error'
        }),
        uiButtonProps: {
          id: 'hidden-talk-to-sales-button'
        }
      })
    }), adminModalOpen && /*#__PURE__*/_jsx(AdminModal, {
      alertStore: alertStore.current,
      apiName: apiName,
      modalType: 'requestSKU',
      trackingData: trackingData,
      onClose: () => setAdminModalOpen(false)
    }), /*#__PURE__*/_jsx(Button, Object.assign({
      "data-test-id": dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'buy-now-button',
      "data-selenium-id": dataSeleniumId,
      truncate: true,
      truncateStringProps: {
        justifyContent: 'center'
      }
    }, uiButtonProps, {
      onClick: () => {
        void handleClick();
      },
      children: buttonText
    }))]
  });
};
export const BuyingButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.BUYING_BUTTON,
  children: /*#__PURE__*/_jsx(BuyingButtonBase, Object.assign({}, props))
});